import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// Path imports, not the @mui/icons-material barrel (see EmailPreviewDialog).
import AttachFile from '@mui/icons-material/AttachFile';
import Delete from '@mui/icons-material/Delete';
import CloudQueue from '@mui/icons-material/CloudQueue';
import { EmailAttachment } from '../../../../types/email';
import { SavedRemarkFilePreview } from './useQuoteAttachments';

const DRIVE_FOLDER_URL = 'https://drive.google.com/drive/folders/1x91bDgsLUp9H71JQWKH2vn8ouBeLVyK8';

/** Drag-and-drop or click-to-pick local files, plus the shared Drive folder. */
function DropZone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [isDragging, setIsDragging] = useState(false);

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      onFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFiles(Array.from(e.target.files));
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'stretch', mt: 1 }}>
        {/* Drag and drop zone */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            border: '1.5px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.400',
            borderRadius: 1,
            textAlign: 'center',
            bgcolor: (t) => (isDragging ? alpha(t.palette.primary.main, 0.06) : t.palette.grey[50]),
            cursor: 'pointer',
            flex: '1 1 180px',
            minHeight: 64,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            transition: 'border-color 120ms, background-color 120ms',
            '&:hover': { borderColor: 'primary.main' },
          }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            type="file"
            id="file-input"
            multiple
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <AttachFile color="action" fontSize="small" />
          <Typography variant="body2" color="textSecondary">
            Drag & drop or click to attach local files
          </Typography>
        </Box>

        {/* Google Drive Link Button */}
        <Button
          variant="outlined"
          startIcon={<CloudQueue />}
          onClick={() => window.open(DRIVE_FOLDER_URL, '_blank', 'noopener,noreferrer')}
          sx={{ height: 'auto', minHeight: 40, flex: '0 0 auto' }}
          title="Open Google Drive folder (Download file first, then attach here)"
        >
          Open Drive Folder
        </Button>
      </Box>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
        For Google Drive files, download them first, then attach using the left box
      </Typography>
    </>
  );
}

interface AttachmentsPanelProps {
  attachPdf: boolean;
  onAttachPdfChange: (checked: boolean) => void;
  attachPdfDisabled: boolean;
  savedRemarkFiles: SavedRemarkFilePreview[];
  excludedSavedRemarkFileIds: Set<number>;
  onToggleSavedRemarkFile: (fileId: number) => void;
  /** Seven Seas: saved-remark files are never attached and cannot be re-added. */
  remarkFilesExcluded: boolean;
  fileAttachments: EmailAttachment[];
  onRemoveAttachment: (index: number) => void;
  onAddFiles: (files: File[]) => void;
}

/** The Attachments column: quote PDF, saved-remark files, and added files. */
export default function AttachmentsPanel({
  attachPdf,
  onAttachPdfChange,
  attachPdfDisabled,
  savedRemarkFiles,
  excludedSavedRemarkFileIds,
  onToggleSavedRemarkFile,
  remarkFilesExcluded,
  fileAttachments,
  onRemoveAttachment,
  onAddFiles,
}: AttachmentsPanelProps) {
  return (
    <Box>
      <Typography variant="subtitle2" component="h3">
        Attachments
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={attachPdf}
            onChange={(e) => onAttachPdfChange(e.target.checked)}
            disabled={attachPdfDisabled}
          />
        }
        label="Attach Quote PDF"
      />

      {/* Files carried by the saved remarks used on this RFQ. Attached by
          the backend at send time - listed here so they are visible and
          can be dropped before sending. */}
      {savedRemarkFiles.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 1, px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {remarkFilesExcluded
              ? 'From Saved Remarks (not attached — Seven Seas requires these to be sent to the user separately):'
              : 'From Saved Remarks:'}
          </Typography>
          <List dense disablePadding>
            {savedRemarkFiles.map((file) => {
              const excluded = remarkFilesExcluded || excludedSavedRemarkFileIds.has(file.id);
              return (
                <ListItem key={file.id} disableGutters sx={{ pr: 6 }}>
                  <ListItemText
                    primary={file.name}
                    primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                    secondary={excluded ? 'Will not be attached' : 'Will be attached'}
                    sx={{
                      textDecoration: excluded ? 'line-through' : 'none',
                      opacity: excluded ? 0.6 : 1
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label={excluded ? `Re-attach ${file.name}` : `Remove ${file.name}`}
                      onClick={() => onToggleSavedRemarkFile(file.id)}
                      disabled={remarkFilesExcluded}
                    >
                      {excluded ? <AttachFile /> : <Delete />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {/* File attachments list */}
      {fileAttachments.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 1, px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Additional Attachments:
          </Typography>
          <List dense disablePadding>
            {fileAttachments.map((file, index) => (
              <ListItem key={index} disableGutters sx={{ pr: 6 }}>
                <ListItemText primary={file.name} primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => onRemoveAttachment(index)} aria-label={`Remove attachment ${file.name}`}>
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <DropZone onFiles={onAddFiles} />
    </Box>
  );
}
